/** @jsxImportSource @emotion/react */
import { debounce } from 'lodash';
import {
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { createStyles, Input, Icon, useTheme } from '../../';
import { Option } from './components';

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  options: SelectOption[];
  multiple?: boolean;
  disabled?: boolean;
  onSelect: (selection: string[]) => void;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  values?: string[];
  helperText?: string;
  error?: boolean;
}

export const Select = ({
  options = [],
  multiple = false,
  disabled,
  onSelect,
  onKeyDown,
  label,
  values,
  helperText,
  error,
}: Props) => {
  const { spacing } = useTheme();
  const styles = useStyles();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const optionsRefs = useRef<(HTMLLIElement | null)[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const useLostFocus = useRef(false);
  const selectedOptions = useMemo(() => values ?? [], [values]);

  const handleOptionClick = ({ value }: SelectOption) => {
    if (multiple) {
      const newSelected = selectedOptions.includes(value)
        ? selectedOptions.filter((o) => o !== value)
        : [...selectedOptions, value];
      onSelect(newSelected);
    } else {
      const newSelected =
        selectedOptions.length && value === selectedOptions[0] ? [] : [value];
      onSelect(newSelected);
      setOpen(false);
    }
    setHighlightedIndex(-1);
    setSearch('');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resetSearch = useCallback(
    debounce(() => {
      setSearch('');
    }, 1000),
    [setSearch]
  );

  useEffect(() => {
    resetSearch();
  }, [resetSearch, search]);

  useEffect(() => {
    if (!search) return;

    const index = options.findIndex((o) =>
      o.label.toLowerCase().startsWith(search.toLowerCase())
    );
    if (index !== -1) {
      setHighlightedIndex(index);
    }
  }, [search, options]);

  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRefs.current[highlightedIndex]) {
      optionsRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  const selectedLabels = useMemo(
    () =>
      options
        .filter((o) => selectedOptions.includes(o.value))
        .map((o) => o.label),
    [options, selectedOptions]
  );

  return (
    <div
      css={[styles.wrapper, disabled && styles.disabled]}
      tabIndex={0}
      onClick={() => !disabled && !open && setOpen(true)}
      onBlur={() => {
        setTimeout(() => {
          setOpen(false);
          setHighlightedIndex(-1);
        }, 100);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Tab') {
          useLostFocus.current = true;
          return;
        }

        if (!open) {
          setOpen(true);
        }

        const lastIndex = options.length - 1;

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setHighlightedIndex((prev) => (prev < lastIndex ? prev + 1 : 0));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : lastIndex));
            break;
          case 'Enter':
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < options.length) {
              handleOptionClick(options[highlightedIndex]);
            }
            break;
          case 'Escape':
            setOpen(false);
            setHighlightedIndex(-1);
            break;
          default: {
            const key = e.key.length === 1 ? e.key : '';
            if (/^[a-z0-9 ]$/i.test(key)) {
              setSearch((prev) => prev + key);
            }
          }
        }

        onKeyDown?.(e);
      }}
    >
      <Input
        label={label}
        value={selectedLabels.join(', ')}
        endAdornment={
          <div css={{ svg: { transform: `rotate(${open ? '' : '-'}90deg)` } }}>
            <Icon name="chevron" size={spacing.xxs} />
          </div>
        }
        disabled={disabled}
        helperText={helperText}
        error={error}
        inputRef={inputRef}
        onFocus={() => {
          if (useLostFocus.current) {
            useLostFocus.current = false;
            return;
          }
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
      />
      {open && (
        <ul
          css={[
            styles.dropdown,
            {
              top: `${(inputRef.current?.getBoundingClientRect()?.height ?? 54) + 4}px`,
            },
          ]}
        >
          {options.map((o, idx) => (
            <Option
              key={o.value}
              option={o}
              selected={selectedOptions.includes(o.value)}
              highlighted={idx === highlightedIndex}
              onClick={(o) => handleOptionClick(o)}
              optionRef={(el) => (optionsRefs.current[idx] = el)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const useStyles = createStyles(({ palette, spacing, shape }) => ({
  wrapper: {
    position: 'relative',
    cursor: 'pointer',
    width: '100%',
    input: {
      cursor: 'pointer',
      userSelect: 'none',
      caretColor: 'transparent',
    },
  },
  disabled: {
    cursor: 'default',
    input: {
      cursor: 'default',
      userSelect: 'none',
    },
  },
  dropdown: {
    position: 'absolute',
    zIndex: 999,
    width: '100%',
    maxHeight: `calc(5.5 * ${spacing.s})`,
    overflowY: 'scroll',
    borderRadius: shape.borderRadius,
    border: `1px solid ${palette.grey.x100}`,
    background: palette.white,
    padding: 0,
    margin: 0,
    'li:first-of-type': { borderTop: 'none' },
  },
}));
